/**
 * メールマーケティング管理画面
 *
 * 管理者用のメール配信一元管理ページ
 * タブ構成: 新規作成 / テンプレート / 送信履歴 / 効果分析
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Tabs,
  Typography,
  Spin,
  message,
  Row,
  Col,
  Select,
  Input,
  Button,
  Checkbox,
  DatePicker,
  Space,
  Tag,
  Table,
  Modal,
  Tooltip,
  Empty,
  Divider,
  Popconfirm,
  Badge,
  Statistic,
  Upload,
  Form,
  Progress,
  Descriptions,
  Alert,
  Breadcrumb,
  Card,
} from 'antd';
import {
  MailOutlined,
  EditOutlined,
  FileTextOutlined,
  HistoryOutlined,
  BarChartOutlined,
  TeamOutlined,
  SendOutlined,
  EyeOutlined,
  RobotOutlined,
  ReloadOutlined,
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  PictureOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  FireOutlined,
  CrownOutlined,
  UserAddOutlined,
  GiftOutlined,
  CreditCardOutlined,
  TrophyOutlined,
  CalendarOutlined,
  LoginOutlined,
  SearchOutlined,
  FileSearchOutlined,
  SwapOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Helmet } from 'react-helmet';
import { emailMarketingApi } from '../../../api';
import './EmailMarketing.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TextArea } = Input;

// プリセットアイコンマッピング
const PRESET_ICONS = {
  FireOutlined: <FireOutlined />,
  CrownOutlined: <CrownOutlined />,
  UserAddOutlined: <UserAddOutlined />,
  ClockCircleOutlined: <ClockCircleOutlined />,
  CreditCardOutlined: <CreditCardOutlined />,
  GiftOutlined: <GiftOutlined />,
  TrophyOutlined: <TrophyOutlined />,
  CalendarOutlined: <CalendarOutlined />,
  TeamOutlined: <TeamOutlined />,
};

// プラン一覧
const PLANS = [
  { value: 'free', label: 'フリー' },
  { value: 'lite', label: 'ライト' },
  { value: 'standard', label: 'スタンダード' },
  { value: 'custom', label: 'カスタム' },
];

// ランク一覧
const RANKS = [
  { value: 'white_1', label: 'ホワイト I' },
  { value: 'white_2', label: 'ホワイト II' },
  { value: 'white_3', label: 'ホワイト III' },
  { value: 'white_4', label: 'ホワイト IV' },
  { value: 'white_5', label: 'ホワイト V' },
  { value: 'bronze_1', label: 'ブロンズ I' },
  { value: 'bronze_2', label: 'ブロンズ II' },
  { value: 'bronze_3', label: 'ブロンズ III' },
  { value: 'silver_1', label: 'シルバー I' },
  { value: 'silver_2', label: 'シルバー II' },
  { value: 'silver_3', label: 'シルバー III' },
  { value: 'gold_1', label: 'ゴールド I' },
  { value: 'gold_2', label: 'ゴールド II' },
  { value: 'gold_3', label: 'ゴールド III' },
  { value: 'platinum_1', label: 'プラチナ I' },
  { value: 'platinum_2', label: 'プラチナ II' },
  { value: 'platinum_3', label: 'プラチナ III' },
  { value: 'diamond_1', label: 'ダイヤモンド I' },
  { value: 'diamond_2', label: 'ダイヤモンド II' },
  { value: 'diamond_3', label: 'ダイヤモンド III' },
  { value: 'legend', label: 'レジェンド' },
];

// テンプレートカテゴリ
const TEMPLATE_CATEGORIES = {
  onboarding: { label: 'オンボーディング', icon: <UserAddOutlined />, color: 'green' },
  retention: { label: 'リテンション', icon: <FireOutlined />, color: 'volcano' },
  promotion: { label: 'プロモーション', icon: <GiftOutlined />, color: 'blue' },
  announcement: { label: 'お知らせ', icon: <MailOutlined />, color: 'purple' },
  custom: { label: 'カスタム', icon: <EditOutlined />, color: 'default' },
};

// 差し込み変数
const MAIL_VARIABLES = [
  { key: '{{user.last_name}}', label: '姓' },
  { key: '{{user.first_name}}', label: '名' },
  { key: '{{user.company_name}}', label: '会社名' },
  { key: '{{user.rank}}', label: 'ランク' },
  { key: '{{user.plan}}', label: 'プラン' },
];

// Quillエディター設定
const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    [{ color: [] }, { background: [] }],
    ['clean'],
  ],
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'bullet',
  'link',
  'image',
  'color',
  'background',
];

/**
 * メインコンポーネント
 */
const EmailMarketing = () => {
  const [activeTab, setActiveTab] = useState('new');
  const [loading, setLoading] = useState(false);
  const quillRef = useRef(null);
  
  // プリセット
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState(null);
  
  // ターゲティング
  const [filters, setFilters] = useState({
    plans: [],
  });
  const [targetUsers, setTargetUsers] = useState([]);
  const [targetCount, setTargetCount] = useState(0);
  
  // キャンペーン
  const [subject, setSubject] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [excludeUserIds, setExcludeUserIds] = useState([]);
  
  // テンプレート
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateModalVisible, setTemplateModalVisible] = useState(false);
  const [templateModalMode, setTemplateModalMode] = useState('create');
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm] = Form.useForm();
  
  // 送信履歴
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignDetailVisible, setCampaignDetailVisible] = useState(false);
  const [campaignRecipients, setCampaignRecipients] = useState([]);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  
  // モーダル
  const [recipientsModalVisible, setRecipientsModalVisible] = useState(false);
  const [testEmailModalVisible, setTestEmailModalVisible] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  // 画像
  const [images, setImages] = useState([]);
  const [imageModalVisible, setImageModalVisible] = useState(false);

  // AI機能
  const [aiGenerateModalVisible, setAiGenerateModalVisible] = useState(false);
  const [aiRewriteModalVisible, setAiRewriteModalVisible] = useState(false);

  // プレビュー用サンプルデータ
  const SAMPLE_DATA = {
    last_name: '山田',
    first_name: '太郎',
    company_name: '株式会社サンプル',
    email: 'sample@example.com',
    rank_display: 'シルバー I',
    plan_display: 'スタンダードプラン',
  };

  // 変数展開関数（プレビュー用）
  const expandVariables = (content) => {
    if (!content) return content;
    
    // 最初の対象ユーザーがいればそのデータを使用、なければサンプルデータ
    const userData = targetUsers.length > 0 ? {
      last_name: targetUsers[0].last_name || SAMPLE_DATA.last_name,
      first_name: targetUsers[0].first_name || SAMPLE_DATA.first_name,
      company_name: targetUsers[0].company_name || SAMPLE_DATA.company_name,
      email: targetUsers[0].email || SAMPLE_DATA.email,
      rank_display: targetUsers[0].rank_display || SAMPLE_DATA.rank_display,
      plan_display: targetUsers[0].plan_display_name || SAMPLE_DATA.plan_display,
    } : SAMPLE_DATA;

    return content
      .replace(/\{\{user\.last_name\}\}/g, userData.last_name)
      .replace(/\{\{user\.first_name\}\}/g, userData.first_name)
      .replace(/\{\{user\.full_name\}\}/g, `${userData.last_name} ${userData.first_name}`)
      .replace(/\{\{user\.company_name\}\}/g, userData.company_name)
      .replace(/\{\{user\.email\}\}/g, userData.email)
      .replace(/\{\{user\.rank\}\}/g, userData.rank_display)
      .replace(/\{\{user\.plan\}\}/g, userData.plan_display);
  };
  const [aiLoading, setAiLoading] = useState(false);
  const [aiGenerateForm] = Form.useForm();
  const [aiRewriteForm] = Form.useForm();

  // 初期ロード
  useEffect(() => {
    loadPresets();
    loadTemplates();
  }, []);

  // プリセット変更時にフィルターを更新し、対象者数を取得
  useEffect(() => {
    if (selectedPreset) {
      const preset = presets.find(p => p.name === selectedPreset);
      if (preset && preset.filters) {
        const presetFilters = { ...preset.filters };
        
        // days_ago系のフィルターから日付を計算してセット
        const now = dayjs();
        if (presetFilters.registered_days_ago_max) {
          presetFilters.registered_date_from = now.subtract(presetFilters.registered_days_ago_max, 'day').toISOString();
          presetFilters.registered_date_to = now.toISOString();
        }
        if (presetFilters.registered_days_ago_min) {
          presetFilters.registered_date_to = now.subtract(presetFilters.registered_days_ago_min, 'day').toISOString();
        }
        if (presetFilters.last_login_days_ago_max) {
          presetFilters.last_login_from = now.subtract(presetFilters.last_login_days_ago_max, 'day').toISOString();
          presetFilters.last_login_to = now.toISOString();
        }
        if (presetFilters.last_login_days_ago_min) {
          presetFilters.last_login_to = now.subtract(presetFilters.last_login_days_ago_min, 'day').toISOString();
        }
        
        setFilters(presetFilters);
      }
      loadTargetCount();
    }
  }, [selectedPreset, presets]);

  // フィルター変更時に対象者数を更新
  useEffect(() => {
    const timer = setTimeout(() => {
      loadTargetCount();
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  // --- データ取得 ---
  const loadPresets = async () => {
    try {
      const data = await emailMarketingApi.getPresets();
      setPresets(data.presets || []);
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await emailMarketingApi.getTemplates();
      setTemplates(data.templates || []);
    } catch (error) {
      console.error('Failed to load templates:', error);
    }
  };

  const loadTargetCount = async () => {
    try {
      const data = await emailMarketingApi.previewTargeting(filters, selectedPreset, 1, 0);
      setTargetCount(data.total_count || 0);
    } catch (error) {
      console.error('Failed to load target count:', error);
    }
  };

  const loadTargetUsers = async () => {
    setLoading(true);
    try {
      const data = await emailMarketingApi.previewTargeting(filters, selectedPreset, 500, 0);
      setTargetUsers(data.users || []);
      setTargetCount(data.total_count || 0);
    } catch (error) {
      console.error('Failed to load target users:', error);
      message.error('対象者の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const loadCampaigns = async () => {
    setLoading(true);
    try {
      const data = await emailMarketingApi.getCampaigns(null, 0, 50);
      setCampaigns(data.campaigns || []);
    } catch (error) {
      console.error('Failed to load campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCampaignRecipients = async (campaignId) => {
    setRecipientsLoading(true);
    try {
      const data = await emailMarketingApi.getCampaignRecipients(campaignId, null, 0, 100);
      setCampaignRecipients(data.recipients || []);
    } catch (error) {
      console.error('Failed to load campaign recipients:', error);
    } finally {
      setRecipientsLoading(false);
    }
  };

  const loadImages = async () => {
    try {
      const data = await emailMarketingApi.getImages(0, 100);
      setImages(data.images || []);
    } catch (error) {
      console.error('Failed to load images:', error);
    }
  };

  // --- アクション ---
  const handlePresetChange = (value) => {
    setSelectedPreset(value);
    if (!value) {
      setFilters({ plans: [] });
    }
  };

  const handlePlanChange = (checkedValues) => {
    setFilters(prev => ({ ...prev, plans: checkedValues }));
    setSelectedPreset(null);
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (template) {
      setSubject(template.subject_template);
      setHtmlContent(template.html_template);
      setSelectedTemplate(templateId);
      message.success('テンプレートを読み込みました');
    }
  };

  // 差し込み変数を挿入
  const insertVariable = (variable) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      quill.insertText(range.index, variable);
      quill.setSelection(range.index + variable.length);
    }
  };

  // テンプレート作成/編集モーダルを開く
  const openTemplateModal = (mode, template = null) => {
    setTemplateModalMode(mode);
    setEditingTemplate(template);
    
    if (mode === 'edit' && template) {
      templateForm.setFieldsValue({
        name: template.name,
        category: template.category,
        description: template.description,
        subject_template: template.subject_template,
        html_template: template.html_template,
      });
    } else {
      templateForm.resetFields();
      if (subject || htmlContent) {
        templateForm.setFieldsValue({
          subject_template: subject,
          html_template: htmlContent,
        });
      }
    }
    
    setTemplateModalVisible(true);
  };

  // テンプレート保存
  const handleSaveTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      
      if (templateModalMode === 'create') {
        await emailMarketingApi.createTemplate(values);
        message.success('テンプレートを作成しました');
      } else {
        await emailMarketingApi.updateTemplate(editingTemplate.id, values);
        message.success('テンプレートを更新しました');
      }
      
      setTemplateModalVisible(false);
      loadTemplates();
    } catch (error) {
      if (error.errorFields) {
        message.warning('必須項目を入力してください');
      } else {
        message.error('テンプレートの保存に失敗しました');
      }
    }
  };

  // キャンペーンをテンプレート化
  const handleCampaignToTemplate = (campaign) => {
    templateForm.setFieldsValue({
      name: `${campaign.subject} (${dayjs(campaign.sent_at).format('M/D')})`,
      category: 'custom',
      subject_template: campaign.subject,
      html_template: campaign.html_content,
    });
    setTemplateModalMode('create');
    setEditingTemplate(null);
    setTemplateModalVisible(true);
  };

  // キャンペーン詳細を開く
  const openCampaignDetail = async (campaign) => {
    setSelectedCampaign(campaign);
    setCampaignDetailVisible(true);
    await loadCampaignRecipients(campaign.campaign_id);
  };

  const handleSaveAsTemplate = async () => {
    if (!subject || !htmlContent) {
      message.warning('件名と本文を入力してください');
      return;
    }
    openTemplateModal('create');
  };

  const handleTestSend = async () => {
    if (!subject || !htmlContent) {
      message.warning('件名と本文を入力してください');
      return;
    }
    setTestEmailModalVisible(true);
  };

  const handleTestSendConfirm = async () => {
    if (!testEmail) {
      message.warning('送信先メールアドレスを入力してください');
      return;
    }

    setLoading(true);
    try {
      const campaign = await emailMarketingApi.createCampaign({
        subject,
        html_content: htmlContent,
        targeting_filters: filters,
        preset_name: selectedPreset,
        exclude_user_ids: excludeUserIds,
      });
      
      await emailMarketingApi.sendCampaign(campaign.campaign_id, true, [testEmail]);
      message.success(`テストメールを ${testEmail} に送信しました`);
      setTestEmailModalVisible(false);
    } catch (error) {
      console.error('Test send failed:', error);
      message.error('テスト送信に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!subject || !htmlContent) {
      message.warning('件名と本文を入力してください');
      return;
    }

    if (targetCount === 0) {
      message.warning('送信対象者がいません');
      return;
    }

    Modal.confirm({
      title: 'メール送信確認',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>以下の内容でメールを送信します：</p>
          <ul>
            <li><strong>件名:</strong> {subject}</li>
            <li><strong>送信先:</strong> {targetCount}名</li>
          </ul>
          <p style={{ color: '#ff4d4f' }}>
            この操作は取り消せません。よろしいですか？
          </p>
        </div>
      ),
      okText: '送信する',
      okType: 'danger',
      cancelText: 'キャンセル',
      onOk: async () => {
        setLoading(true);
        try {
          const campaign = await emailMarketingApi.createCampaign({
            subject,
            html_content: htmlContent,
            targeting_filters: filters,
            preset_name: selectedPreset,
            exclude_user_ids: excludeUserIds,
          });
          
          const result = await emailMarketingApi.sendCampaign(campaign.campaign_id, false, []);
          message.success(result.message || `${result.total_sent}件のメール送信を開始しました`);
          
          setSubject('');
          setHtmlContent('');
          setSelectedTemplate(null);
          
          loadCampaigns();
        } catch (error) {
          console.error('Send failed:', error);
          message.error('メール送信に失敗しました');
        } finally {
          setLoading(false);
        }
      },
    });
  };

  const handleExcludeUser = (userId) => {
    if (excludeUserIds.includes(userId)) {
      setExcludeUserIds(prev => prev.filter(id => id !== userId));
    } else {
      setExcludeUserIds(prev => [...prev, userId]);
    }
  };

  // 画像圧縮ユーティリティ関数
  const compressImage = (file, maxWidth = 800, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          // 幅が最大値を超える場合はリサイズ
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                // 元のファイル名を保持した新しいFileオブジェクトを作成
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now(),
                });
                resolve(compressedFile);
              } else {
                reject(new Error('画像の圧縮に失敗しました'));
              }
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('ファイルの読み込みに失敗しました'));
      reader.readAsDataURL(file);
    });
  };

  // 画像アップロード（圧縮付き）
  const handleImageUpload = async (file) => {
    try {
      // 画像を圧縮（最大幅800px、品質85%）
      const compressedFile = await compressImage(file, 800, 0.85);
      const originalSize = (file.size / 1024).toFixed(1);
      const compressedSize = (compressedFile.size / 1024).toFixed(1);
      
      const result = await emailMarketingApi.uploadImage(compressedFile);
      message.success(`画像をアップロードしました（${originalSize}KB → ${compressedSize}KB）`);
      loadImages();
      return result;
    } catch (error) {
      message.error('画像のアップロードに失敗しました');
      throw error;
    }
  };

  // AI生成
  const handleAiGenerate = async () => {
    try {
      const values = await aiGenerateForm.validateFields();
      setAiLoading(true);
      
      const result = await emailMarketingApi.generateEmail(
        values.purpose,
        values.tone || 'neutral',
        selectedPreset,
        values.additional_instructions
      );
      
      setSubject(result.subject);
      setHtmlContent(result.html_content);
      setAiGenerateModalVisible(false);
      message.success('メールを生成しました');
      aiGenerateForm.resetFields();
    } catch (error) {
      if (error.errorFields) {
        message.warning('目的を入力してください');
      } else {
        console.error('AI generate failed:', error);
        message.error('メール生成に失敗しました');
      }
    } finally {
      setAiLoading(false);
    }
  };

  // AIリライト
  const handleAiRewrite = async () => {
    if (!subject || !htmlContent) {
      message.warning('リライトするメールの件名と本文を入力してください');
      return;
    }
    
    try {
      const values = await aiRewriteForm.validateFields();
      setAiLoading(true);
      
      const result = await emailMarketingApi.rewriteEmail(
        subject,
        htmlContent,
        values.directions,
        values.custom_instruction
      );
      
      setSubject(result.subject);
      setHtmlContent(result.html_content);
      setAiRewriteModalVisible(false);
      message.success('メールをリライトしました');
      aiRewriteForm.resetFields();
    } catch (error) {
      if (error.errorFields) {
        message.warning('リライトの方向性を選択してください');
      } else {
        console.error('AI rewrite failed:', error);
        message.error('リライトに失敗しました');
      }
    } finally {
      setAiLoading(false);
    }
  };

  // --- タブコンテンツ ---

  // 新規作成タブ
  const renderNewCampaignTab = () => {
    const selectedPresetInfo = selectedPreset ? presets.find(p => p.name === selectedPreset) : null;
    
    return (
    <div className="new-campaign-layout">
      {/* ターゲティングパネル */}
      <div className="targeting-panel">
        <div className="panel-title">
          <TeamOutlined /> 対象者設定
        </div>

        {/* プリセット選択 */}
        <div className="preset-section">
          <div className="section-label">プリセット</div>
          <Select
            placeholder="セグメントを選択..."
            value={selectedPreset}
            onChange={handlePresetChange}
            allowClear
            style={{ width: '100%' }}
          >
            {presets.map(preset => (
              <Option key={preset.name} value={preset.name}>
                <Space>
                  {PRESET_ICONS[preset.icon] || <TeamOutlined />}
                  {preset.display_name}
                </Space>
              </Option>
            ))}
          </Select>
          {selectedPresetInfo && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
              {selectedPresetInfo.description}
            </div>
          )}
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* 詳細フィルター */}
        <div className="filter-section">
          <div className="filter-label">プラン</div>
          <Checkbox.Group
            value={filters.plans || []}
            onChange={(values) => {
              setFilters(prev => ({ ...prev, plans: values }));
              setSelectedPreset(null);
            }}
            className="checkbox-group"
          >
            {PLANS.map(plan => (
              <Checkbox key={plan.value} value={plan.value}>
                {plan.label}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>

        <div className="filter-section">
          <div className="filter-label">登録日</div>
          <RangePicker
            className="date-range-picker"
            size="small"
            placeholder={['開始日', '終了日']}
            value={
              filters.registered_date_from && filters.registered_date_to
                ? [dayjs(filters.registered_date_from), dayjs(filters.registered_date_to)]
                : undefined
            }
            onChange={(dates) => {
              if (dates) {
                setFilters(prev => ({
                  ...prev,
                  registered_date_from: dates[0]?.toISOString(),
                  registered_date_to: dates[1]?.toISOString(),
                  registered_days_ago_min: undefined,
                  registered_days_ago_max: undefined,
                }));
              } else {
                setFilters(prev => {
                  const { registered_date_from, registered_date_to, ...rest } = prev;
                  return rest;
                });
              }
              setSelectedPreset(null);
            }}
          />
        </div>

        <div className="filter-section">
          <div className="filter-label">最終ログイン</div>
          <RangePicker
            className="date-range-picker"
            size="small"
            placeholder={['開始日', '終了日']}
            value={
              filters.last_login_from && filters.last_login_to
                ? [dayjs(filters.last_login_from), dayjs(filters.last_login_to)]
                : undefined
            }
            onChange={(dates) => {
              if (dates) {
                setFilters(prev => ({
                  ...prev,
                  last_login_from: dates[0]?.toISOString(),
                  last_login_to: dates[1]?.toISOString(),
                  last_login_days_ago_min: undefined,
                  last_login_days_ago_max: undefined,
                }));
              } else {
                setFilters(prev => {
                  const { last_login_from, last_login_to, ...rest } = prev;
                  return rest;
                });
              }
              setSelectedPreset(null);
            }}
          />
        </div>

        <div className="filter-section">
          <div className="filter-label">ランク範囲</div>
          <Space style={{ width: '100%' }}>
            <Select
              placeholder="最小"
              allowClear
              size="small"
              style={{ width: 100 }}
              value={filters.rank_min}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, rank_min: value }));
                setSelectedPreset(null);
              }}
            >
              {RANKS.map(rank => (
                <Option key={rank.value} value={rank.value}>{rank.label}</Option>
              ))}
            </Select>
            <span>〜</span>
            <Select
              placeholder="最大"
              allowClear
              size="small"
              style={{ width: 100 }}
              value={filters.rank_max}
              onChange={(value) => {
                setFilters(prev => ({ ...prev, rank_max: value }));
                setSelectedPreset(null);
              }}
            >
              {RANKS.map(rank => (
                <Option key={rank.value} value={rank.value}>{rank.label}</Option>
              ))}
            </Select>
          </Space>
        </div>

        {/* 対象者数 */}
        <div className="target-count-box">
          <div className="count-number">{targetCount.toLocaleString()}</div>
          <div className="count-label">名が対象</div>
        </div>

        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={() => {
            loadTargetUsers();
            setRecipientsModalVisible(true);
          }}
          style={{ marginTop: 8, padding: 0 }}
        >
          対象者を確認
        </Button>
      </div>

      {/* エディターパネル */}
      <div className="editor-panel">
        <div className="subject-input">
          <Input
            placeholder="件名を入力..."
            value={subject}
            onChange={e => setSubject(e.target.value)}
            prefix={<MailOutlined style={{ color: '#bbb' }} />}
            size="large"
          />
        </div>

        <div className="ai-buttons">
          <Button 
            icon={<RobotOutlined />} 
            onClick={() => setAiGenerateModalVisible(true)}
          >
            AIで生成
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => {
              if (!subject || !htmlContent) {
                message.warning('リライトするメールを先に入力してください');
                return;
              }
              setAiRewriteModalVisible(true);
            }}
          >
            AIでリライト
          </Button>
          <Button
            icon={<PictureOutlined />}
            onClick={() => {
              loadImages();
              setImageModalVisible(true);
            }}
          >
            画像
          </Button>
        </div>

        <div className="content-editor">
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={htmlContent}
            onChange={setHtmlContent}
            modules={quillModules}
            formats={quillFormats}
            placeholder="メール本文を入力..."
          />
        </div>

        <div className="template-loader">
          <Text type="secondary">テンプレートから読み込み:</Text>
          <Select
            placeholder="テンプレートを選択..."
            value={selectedTemplate}
            onChange={handleTemplateSelect}
            allowClear
            style={{ flex: 1 }}
          >
            {templates.map(template => (
              <Option key={template.id} value={template.id}>
                {template.name}
              </Option>
            ))}
          </Select>
        </div>

        {/* 差し込み変数 */}
        <div className="variables-section">
          <Text type="secondary" style={{ marginRight: 8, fontSize: 12 }}>差し込み変数:</Text>
          {MAIL_VARIABLES.map(v => (
            <Tag
              key={v.key}
              style={{ cursor: 'pointer', marginBottom: 4 }}
              onClick={() => insertVariable(v.key)}
            >
              {v.label}
            </Tag>
          ))}
        </div>
      </div>

      {/* プレビューパネル */}
      <div className="preview-panel">
        <div className="panel-title">
          <EyeOutlined /> プレビュー
        </div>

        <div className="email-preview-frame">
          {subject ? (
            <>
              <div className="preview-subject">{expandVariables(subject)}</div>
              <div
                className="preview-body"
                dangerouslySetInnerHTML={{ __html: expandVariables(htmlContent) || '<p style="color: #999;">本文を入力してください</p>' }}
              />
            </>
          ) : (
            <Empty description="件名と本文を入力してください" />
          )}
        </div>

        <div className="recipient-summary">
          <div className="summary-row">
            <span>送信先</span>
            <span><strong>{targetCount.toLocaleString()}</strong> <span style={{ fontSize: 12 }}>名</span></span>
          </div>
          {excludeUserIds.length > 0 && (
            <div className="summary-row" style={{ marginTop: 4 }}>
              <span>除外</span>
              <span style={{ color: '#ff4d4f' }}>{excludeUserIds.length} <span style={{ fontSize: 12 }}>名</span></span>
            </div>
          )}
        </div>

        <div className="action-buttons">
          <Button
            icon={<SaveOutlined />}
            onClick={handleSaveAsTemplate}
          >
            テンプレート保存
          </Button>
          <Button
            icon={<SendOutlined />}
            onClick={handleTestSend}
          >
            テスト送信
          </Button>
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={loading}
            disabled={!subject || !htmlContent || targetCount === 0}
          >
            送信実行
          </Button>
        </div>
      </div>
    </div>
  );
  };

  // テンプレートタブ
  const renderTemplatesTab = () => (
    <div className="templates-tab">
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button 
          type="primary" 
          icon={<PlusOutlined />}
          onClick={() => openTemplateModal('create')}
        >
          新規テンプレート作成
        </Button>
        <Text type="secondary">
          {templates.length}件のテンプレート
        </Text>
      </div>

      {Object.entries(TEMPLATE_CATEGORIES).map(([categoryKey, { label, icon }]) => {
        const categoryTemplates = templates.filter(t => t.category === categoryKey);
        if (categoryTemplates.length === 0) return null;

        return (
          <div key={categoryKey} className="category-section">
            <div className="category-header">
              {icon} {label}
              <Tag style={{ marginLeft: 8 }}>{categoryTemplates.length}</Tag>
            </div>
            {categoryTemplates.map(template => (
              <div
                key={template.id}
                className="template-card"
              >
                <div 
                  className="template-info"
                  onClick={() => handleTemplateSelect(template.id)}
                  style={{ cursor: 'pointer', flex: 1 }}
                >
                  <div className="template-name">
                    <FileTextOutlined style={{ marginRight: 8 }} />
                    {template.name}
                  </div>
                  <div className="template-meta">
                    使用: {template.usage_count}回
                    {template.last_used_at && (
                      <> · 最終: {dayjs(template.last_used_at).format('M/D')}</>
                    )}
                    {template.description && (
                      <> · {template.description}</>
                    )}
                  </div>
                </div>
                <Space>
                  <Tooltip title="新規作成画面に読み込み">
                    <Button 
                      size="small" 
                      icon={<CopyOutlined />}
                      onClick={() => {
                        handleTemplateSelect(template.id);
                        setActiveTab('new');
                      }}
                    />
                  </Tooltip>
                  <Button 
                    size="small" 
                    icon={<EditOutlined />}
                    onClick={() => openTemplateModal('edit', template)}
                  >
                    編集
                  </Button>
                  <Popconfirm
                    title="このテンプレートを削除しますか？"
                    onConfirm={() => {
                      emailMarketingApi.deleteTemplate(template.id).then(() => {
                        message.success('テンプレートを削除しました');
                        loadTemplates();
                      });
                    }}
                  >
                    <Button size="small" icon={<DeleteOutlined />} danger />
                  </Popconfirm>
                </Space>
              </div>
            ))}
          </div>
        );
      })}

      {templates.length === 0 && (
        <Empty 
          description="テンプレートがありません"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <Button type="primary" onClick={() => openTemplateModal('create')}>
            最初のテンプレートを作成
          </Button>
        </Empty>
      )}
    </div>
  );

  // 送信履歴タブ
  const renderHistoryTab = () => {
    useEffect(() => {
      if (activeTab === 'history') {
        loadCampaigns();
      }
    }, [activeTab]);

    return (
      <div className="history-tab">
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Button 
              icon={<ReloadOutlined />} 
              onClick={loadCampaigns}
              loading={loading}
            >
              更新
            </Button>
          </Space>
          <Text type="secondary">
            {campaigns.length}件のキャンペーン
          </Text>
        </div>

        {loading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : campaigns.length === 0 ? (
          <Empty description="送信履歴がありません" />
        ) : (
          campaigns.map(campaign => (
            <div key={campaign.id} className="campaign-card">
              <div className="card-header">
                <div>
                  <h3 className="campaign-subject">
                    <MailOutlined style={{ marginRight: 8 }} />
                    {campaign.subject}
                  </h3>
                  <div className="campaign-meta">
                    {campaign.sent_at 
                      ? dayjs(campaign.sent_at).format('YYYY/MM/DD HH:mm') 
                      : dayjs(campaign.created_at).format('YYYY/MM/DD HH:mm')
                    } · 送信者: {campaign.sent_by_name || campaign.sent_by}
                    {campaign.preset_name && (
                      <Tag size="small" style={{ marginLeft: 8 }}>
                        {presets.find(p => p.name === campaign.preset_name)?.display_name || campaign.preset_name}
                      </Tag>
                    )}
                  </div>
                </div>
                <Tag 
                  color={
                    campaign.status === 'sent' ? 'green' : 
                    campaign.status === 'failed' ? 'red' : 
                    campaign.status === 'sending' ? 'processing' :
                    'blue'
                  }
                >
                  {campaign.status === 'sent' ? '送信完了' : 
                   campaign.status === 'failed' ? '失敗' : 
                   campaign.status === 'sending' ? '送信中' :
                   campaign.status === 'draft' ? '下書き' :
                   campaign.status}
                </Tag>
              </div>

              <div className="stats-row">
                <div className="stat-item">
                  <div className="stat-value">{campaign.total_recipients.toLocaleString()}<span className="stat-unit">通</span></div>
                  <div className="stat-label">送信数</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {campaign.total_recipients > 0
                      ? Math.round((campaign.opened_count / campaign.total_recipients) * 100)
                      : '-'}<span className="stat-unit">%</span>
                  </div>
                  <div className="stat-label">開封率</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {campaign.total_recipients > 0
                      ? Math.round((campaign.clicked_count / campaign.total_recipients) * 100)
                      : '-'}<span className="stat-unit">%</span>
                  </div>
                  <div className="stat-label">クリック率</div>
                </div>
                <div className="stat-item">
                  <div className="stat-value">
                    {campaign.total_recipients > 0
                      ? Math.round((campaign.successful_deliveries / campaign.total_recipients) * 100)
                      : '-'}<span className="stat-unit">%</span>
                  </div>
                  <div className="stat-label">配信成功</div>
                </div>
              </div>

              <div className="effect-row">
                <div className="effect-item">
                  <LoginOutlined />
                  ログイン: {campaign.effect_logins}
                </div>
                <div className="effect-item">
                  <SearchOutlined />
                  リサーチ: {campaign.effect_researches}
                </div>
                <div className="effect-item">
                  <FileSearchOutlined />
                  記事生成: {campaign.effect_generations}
                </div>
                <div className="effect-item">
                  <SwapOutlined />
                  プラン変更: {campaign.effect_plan_changes}
                </div>
              </div>

              <div className="card-actions">
                <Button 
                  size="small" 
                  icon={<EyeOutlined />}
                  onClick={() => openCampaignDetail(campaign)}
                >
                  詳細
                </Button>
                <Button 
                  size="small" 
                  icon={<SaveOutlined />}
                  onClick={() => handleCampaignToTemplate(campaign)}
                >
                  テンプレート化
                </Button>
                {campaign.status === 'draft' && (
                  <Popconfirm
                    title="この下書きを削除しますか？"
                    onConfirm={() => {
                      emailMarketingApi.deleteCampaign(campaign.campaign_id).then(() => {
                        message.success('キャンペーンを削除しました');
                        loadCampaigns();
                      });
                    }}
                  >
                    <Button size="small" icon={<DeleteOutlined />} danger>
                      削除
                    </Button>
                  </Popconfirm>
                )}
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  // 効果分析タブ
  const [analyticsPeriod, setAnalyticsPeriod] = useState(30);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [trendsData, setTrendsData] = useState([]);
  const [segmentData, setSegmentData] = useState([]);
  const [topCampaigns, setTopCampaigns] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsFilterCampaignId, setAnalyticsFilterCampaignId] = useState(null);
  const [sentCampaigns, setSentCampaigns] = useState([]);

  const loadAnalytics = async (days = analyticsPeriod, campaignId = analyticsFilterCampaignId) => {
    setAnalyticsLoading(true);
    try {
      const [summary, trends, segments, top, campaignList] = await Promise.all([
        emailMarketingApi.getAnalyticsSummary(days, campaignId),
        emailMarketingApi.getAnalyticsTrends(days, campaignId),
        emailMarketingApi.getSegmentPerformance(days),
        emailMarketingApi.getTopCampaigns(days, 5, 'open_rate'),
        emailMarketingApi.getCampaigns('sent', 0, 100),  // 送信済みキャンペーン一覧
      ]);
      setAnalyticsData(summary);
      setTrendsData(trends.trends || []);
      setSegmentData(segments.segments || []);
      setTopCampaigns(top.campaigns || []);
      setSentCampaigns(campaignList.campaigns || []);
    } catch (error) {
      console.error('Failed to load analytics:', error);
      message.error('分析データの取得に失敗しました');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'analytics') {
      loadAnalytics();
    }
  }, [activeTab]);

  const renderAnalyticsTab = () => (
    <div className="analytics-tab">
      {/* フィルター */}
      <div style={{ marginBottom: 24, display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
        <Space>
          <Text>期間:</Text>
          <Select
            value={analyticsPeriod}
            onChange={(value) => {
              setAnalyticsPeriod(value);
              loadAnalytics(value, analyticsFilterCampaignId);
            }}
            style={{ width: 120 }}
          >
            <Option value={7}>過去7日</Option>
            <Option value={30}>過去30日</Option>
            <Option value={90}>過去90日</Option>
          </Select>
        </Space>
        <Space>
          <Text>キャンペーン:</Text>
          <Select
            value={analyticsFilterCampaignId}
            onChange={(value) => {
              setAnalyticsFilterCampaignId(value);
              loadAnalytics(analyticsPeriod, value);
            }}
            style={{ width: 250 }}
            placeholder="全キャンペーン"
            allowClear
          >
            {sentCampaigns.map(c => (
              <Option key={c.campaign_id} value={c.campaign_id}>
                {c.subject.length > 30 ? c.subject.substring(0, 30) + '...' : c.subject}
              </Option>
            ))}
          </Select>
        </Space>
        <Button icon={<ReloadOutlined />} onClick={() => loadAnalytics()} loading={analyticsLoading}>
          更新
        </Button>
      </div>

      {analyticsLoading && !analyticsData ? (
        <div className="loading-container">
          <Spin size="large" />
        </div>
      ) : analyticsData ? (
        <>
          {/* サマリーカード */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="キャンペーン数"
                  value={analyticsData.total_campaigns}
                  suffix={<span style={{ fontSize: 14, color: '#888' }}>件</span>}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="送信数"
                  value={analyticsData.total_sent}
                  suffix={<span style={{ fontSize: 14, color: '#888' }}>通</span>}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="開封率"
                  value={analyticsData.open_rate}
                  suffix={<span style={{ fontSize: 14, color: '#888' }}>%</span>}
                />
              </Card>
            </Col>
            <Col xs={12} sm={6}>
              <Card size="small">
                <Statistic
                  title="クリック率"
                  value={analyticsData.click_rate}
                  suffix={<span style={{ fontSize: 14, color: '#888' }}>%</span>}
                />
              </Card>
            </Col>
          </Row>

          {/* 効果測定サマリー */}
          <Card title="効果測定（48時間以内のユーザーアクション）" size="small" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <Statistic
                  title="ログイン"
                  value={analyticsData.effects?.logins || 0}
                  suffix={<span style={{ fontSize: 12, color: '#888' }}>({analyticsData.effects?.login_rate || 0}%)</span>}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="リサーチ実行"
                  value={analyticsData.effects?.researches || 0}
                  suffix={<span style={{ fontSize: 12, color: '#888' }}>({analyticsData.effects?.research_rate || 0}%)</span>}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="記事生成"
                  value={analyticsData.effects?.generations || 0}
                  suffix={<span style={{ fontSize: 12, color: '#888' }}>({analyticsData.effects?.generation_rate || 0}%)</span>}
                />
              </Col>
              <Col xs={12} sm={6}>
                <Statistic
                  title="プラン変更"
                  value={analyticsData.effects?.plan_changes || 0}
                  suffix={<span style={{ fontSize: 12, color: '#888' }}>({analyticsData.effects?.plan_change_rate || 0}%)</span>}
                />
              </Col>
            </Row>
          </Card>

          <Row gutter={[24, 24]}>
            {/* プリセット（セグメント）別パフォーマンス */}
            <Col xs={24} lg={12}>
              <Card 
                title={
                  <Tooltip title="キャンペーン作成時に使用したプリセット（ターゲット設定）ごとのパフォーマンス">
                    <span>プリセット別パフォーマンス <span style={{ fontSize: 12, color: '#888' }}>(ℹ)</span></span>
                  </Tooltip>
                } 
                size="small"
              >
                {segmentData.length > 0 ? (
                  <Table
                    dataSource={segmentData}
                    rowKey="segment"
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: 'プリセット',
                        dataIndex: 'segment',
                        render: (text) => {
                          const preset = presets.find(p => p.name === text);
                          return preset?.display_name || text || '(指定なし)';
                        },
                      },
                      {
                        title: 'キャンペーン',
                        dataIndex: 'campaigns',
                        width: 80,
                        align: 'center',
                      },
                      {
                        title: '開封率',
                        dataIndex: 'open_rate',
                        width: 80,
                        align: 'center',
                        render: (v) => `${v}%`,
                      },
                      {
                        title: 'クリック率',
                        dataIndex: 'click_rate',
                        width: 80,
                        align: 'center',
                        render: (v) => `${v}%`,
                      },
                    ]}
                  />
                ) : (
                  <Empty description="データがありません" />
                )}
              </Card>
            </Col>

            {/* トップキャンペーン */}
            <Col xs={24} lg={12}>
              <Card title="開封率トップキャンペーン" size="small">
                {topCampaigns.length > 0 ? (
                  <Table
                    dataSource={topCampaigns}
                    rowKey="campaign_id"
                    size="small"
                    pagination={false}
                    columns={[
                      {
                        title: '件名',
                        dataIndex: 'subject',
                        ellipsis: true,
                        render: (text) => (
                          <Tooltip title={text}>
                            {text?.substring(0, 25)}{text?.length > 25 ? '...' : ''}
                          </Tooltip>
                        ),
                      },
                      {
                        title: '開封率',
                        dataIndex: 'open_rate',
                        width: 80,
                        align: 'center',
                        render: (v) => `${v}%`,
                      },
                      {
                        title: 'クリック率',
                        dataIndex: 'click_rate',
                        width: 80,
                        align: 'center',
                        render: (v) => `${v}%`,
                      },
                    ]}
                  />
                ) : (
                  <Empty description="データがありません" />
                )}
              </Card>
            </Col>
          </Row>

          {/* 配信トレンド */}
          {trendsData.length > 0 && (
            <Card title="配信トレンド" size="small" style={{ marginTop: 24 }}>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ display: 'flex', gap: 8, minWidth: Math.max(trendsData.length * 80, 400) }}>
                  {trendsData.map((day) => (
                    <div key={day.date} style={{ flex: 1, minWidth: 70, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                        {dayjs(day.date).format('M/D')}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 600 }}>{day.sent}</div>
                      <div style={{ fontSize: 10, color: '#888' }}>送信</div>
                      {day.delivered > 0 && (
                        <Progress
                          percent={Math.round((day.opened / day.delivered) * 100)}
                          size="small"
                          format={(p) => `${p}%`}
                          style={{ marginTop: 4 }}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </>
      ) : (
        <Empty description="分析データがありません" />
      )}
    </div>
  );

  // タブ定義
  const tabs = [
    {
      key: 'new',
      label: (
        <span>
          <EditOutlined /> 新規作成
        </span>
      ),
      children: renderNewCampaignTab(),
    },
    {
      key: 'templates',
      label: (
        <span>
          <FileTextOutlined /> テンプレート
          {templates.length > 0 && (
            <Badge count={templates.length} style={{ marginLeft: 8 }} size="small" />
          )}
        </span>
      ),
      children: renderTemplatesTab(),
    },
    {
      key: 'history',
      label: (
        <span>
          <HistoryOutlined /> 送信履歴
        </span>
      ),
      children: renderHistoryTab(),
    },
    {
      key: 'analytics',
      label: (
        <span>
          <BarChartOutlined /> 効果分析
        </span>
      ),
      children: renderAnalyticsTab(),
    },
  ];

  return (
    <>
      <Helmet>
        <title>メール配信 | magicss</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Breadcrumb
        style={{ margin: '16px 0' }}
        items={[{ title: '管理者機能' }, { title: 'メール配信' }]}
      />
      <Title level={1} className="gradient-text" style={{ fontSize: '24px' }}>
        メール配信
      </Title>
      <Divider style={{ margin: '24px 0' }} />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabs}
      />

      {/* 対象者確認モーダル */}
      <Modal
        title={`対象者一覧（${targetCount.toLocaleString()}名）`}
        open={recipientsModalVisible}
        onCancel={() => setRecipientsModalVisible(false)}
        width={900}
        footer={[
          <Button key="close" onClick={() => setRecipientsModalVisible(false)}>
            閉じる
          </Button>,
        ]}
      >
        <Table
          dataSource={targetUsers}
          rowKey="user_id"
          size="small"
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: '',
              dataIndex: 'excluded',
              width: 50,
              render: (_, record) => (
                <Checkbox
                  checked={!excludeUserIds.includes(record.user_id)}
                  onChange={() => handleExcludeUser(record.user_id)}
                />
              ),
            },
            {
              title: 'ユーザー',
              dataIndex: 'last_name',
              render: (_, record) => (
                <div>
                  <div>{record.last_name} {record.first_name}</div>
                  <div style={{ fontSize: 12, color: '#888' }}>{record.email}</div>
                </div>
              ),
            },
            {
              title: '会社名',
              dataIndex: 'company_name',
            },
            {
              title: 'プラン',
              dataIndex: 'plan_display_name',
              render: (text) => text || '-',
            },
            {
              title: 'ランク',
              dataIndex: 'rank_display',
              render: (text) => text || '-',
            },
            {
              title: '登録日',
              dataIndex: 'registered_at',
              render: (date) => dayjs(date).format('YYYY/MM/DD'),
            },
            {
              title: '最終ログイン',
              dataIndex: 'last_login_at',
              render: (date) => date ? dayjs(date).format('M/D HH:mm') : '-',
            },
          ]}
        />
      </Modal>

      {/* テスト送信モーダル */}
      <Modal
        title="テスト送信"
        open={testEmailModalVisible}
        onCancel={() => setTestEmailModalVisible(false)}
        onOk={handleTestSendConfirm}
        okText="テスト送信"
        confirmLoading={loading}
      >
        <Form layout="vertical">
          <Form.Item label="送信先メールアドレス" required>
            <Input
              placeholder="test@example.com"
              value={testEmail}
              onChange={e => setTestEmail(e.target.value)}
            />
          </Form.Item>
          <Text type="secondary">
            ※ 件名に【テスト】が付与されます
          </Text>
        </Form>
      </Modal>

      {/* テンプレート作成/編集モーダル */}
      <Modal
        title={templateModalMode === 'create' ? 'テンプレート作成' : 'テンプレート編集'}
        open={templateModalVisible}
        onCancel={() => setTemplateModalVisible(false)}
        onOk={handleSaveTemplate}
        okText="保存"
        width={800}
        destroyOnClose
      >
        <Form form={templateForm} layout="vertical">
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="name"
                label="テンプレート名"
                rules={[{ required: true, message: 'テンプレート名を入力してください' }]}
              >
                <Input placeholder="例: 新機能リリースお知らせ" />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="category"
                label="カテゴリ"
                rules={[{ required: true, message: 'カテゴリを選択してください' }]}
                initialValue="custom"
              >
                <Select>
                  {Object.entries(TEMPLATE_CATEGORIES).map(([key, { label, icon }]) => (
                    <Option key={key} value={key}>
                      <Space>{icon} {label}</Space>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="説明（任意）">
            <Input placeholder="テンプレートの用途や説明" />
          </Form.Item>

          <Form.Item
            name="subject_template"
            label="件名"
            rules={[{ required: true, message: '件名を入力してください' }]}
          >
            <Input placeholder="メールの件名" />
          </Form.Item>

          <Form.Item
            name="html_template"
            label="本文"
            rules={[{ required: true, message: '本文を入力してください' }]}
          >
            <ReactQuill
              theme="snow"
              modules={quillModules}
              formats={quillFormats}
              placeholder="メール本文..."
              style={{ minHeight: 200 }}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* キャンペーン詳細モーダル */}
      <Modal
        title={
          <Space>
            <MailOutlined />
            キャンペーン詳細
          </Space>
        }
        open={campaignDetailVisible}
        onCancel={() => setCampaignDetailVisible(false)}
        width={1000}
        styles={{
          body: {
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
          }
        }}
        footer={[
          <Button 
            key="measure" 
            icon={<BarChartOutlined />} 
            onClick={async () => {
              if (!selectedCampaign?.sent_at) {
                message.warning('送信済みのキャンペーンのみ効果測定できます');
                return;
              }
              try {
                setLoading(true);
                const result = await emailMarketingApi.measureCampaignEffects(selectedCampaign.campaign_id);
                message.success('効果測定が完了しました');
                setSelectedCampaign(prev => ({
                  ...prev,
                  effect_logins: result.effects.logins,
                  effect_researches: result.effects.researches,
                  effect_generations: result.effects.generations,
                  effect_plan_changes: result.effects.plan_changes,
                }));
                loadCampaigns();
              } catch (error) {
                message.error('効果測定に失敗しました');
              } finally {
                setLoading(false);
              }
            }}
            loading={loading}
          >
            効果を再測定
          </Button>,
          <Button key="template" icon={<SaveOutlined />} onClick={() => {
            handleCampaignToTemplate(selectedCampaign);
            setCampaignDetailVisible(false);
          }}>
            テンプレート化
          </Button>,
          <Button key="close" onClick={() => setCampaignDetailVisible(false)}>
            閉じる
          </Button>,
        ]}
      >
        {selectedCampaign && (
          <>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="件名" span={2}>
                <strong>{selectedCampaign.subject}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="送信日時">
                {selectedCampaign.sent_at 
                  ? dayjs(selectedCampaign.sent_at).format('YYYY/MM/DD HH:mm:ss')
                  : '-'
                }
              </Descriptions.Item>
              <Descriptions.Item label="送信者">
                {selectedCampaign.sent_by_name || selectedCampaign.sent_by}
              </Descriptions.Item>
              <Descriptions.Item label="ステータス">
                <Tag color={selectedCampaign.status === 'sent' ? 'green' : 'blue'}>
                  {selectedCampaign.status === 'sent' ? '送信完了' : selectedCampaign.status}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="セグメント">
                {selectedCampaign.preset_name 
                  ? presets.find(p => p.name === selectedCampaign.preset_name)?.display_name || selectedCampaign.preset_name
                  : 'カスタム'
                }
              </Descriptions.Item>
            </Descriptions>

            <Divider>配信統計</Divider>

            <Row gutter={16}>
              <Col span={6}>
                <Statistic title="送信数" value={selectedCampaign.total_recipients} />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="開封率" 
                  value={selectedCampaign.total_recipients > 0 
                    ? Math.round((selectedCampaign.opened_count / selectedCampaign.total_recipients) * 100)
                    : 0
                  }
                  suffix="%" 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="クリック率" 
                  value={selectedCampaign.total_recipients > 0
                    ? Math.round((selectedCampaign.clicked_count / selectedCampaign.total_recipients) * 100)
                    : 0
                  }
                  suffix="%" 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="配信成功率" 
                  value={selectedCampaign.total_recipients > 0
                    ? Math.round((selectedCampaign.successful_deliveries / selectedCampaign.total_recipients) * 100)
                    : 0
                  }
                  suffix="%" 
                />
              </Col>
            </Row>

            <Divider>効果測定（48時間以内）</Divider>

            <Row gutter={16}>
              <Col span={6}>
                <Statistic 
                  title="ログイン" 
                  value={selectedCampaign.effect_logins} 
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="リサーチ実行" 
                  value={selectedCampaign.effect_researches}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="記事生成" 
                  value={selectedCampaign.effect_generations}
                />
              </Col>
              <Col span={6}>
                <Statistic 
                  title="プラン変更" 
                  value={selectedCampaign.effect_plan_changes}
                />
              </Col>
            </Row>

            <Divider>受信者一覧</Divider>

            <Table
              dataSource={campaignRecipients}
              rowKey="id"
              size="small"
              loading={recipientsLoading}
              pagination={{ pageSize: 5, size: 'small' }}
              columns={[
                {
                  title: 'メールアドレス',
                  dataIndex: 'email',
                  width: 200,
                },
                {
                  title: 'ステータス',
                  dataIndex: 'status',
                  width: 100,
                  render: (status) => (
                    <Tag color={
                      status === 'delivered' ? 'green' :
                      status === 'sent' ? 'blue' :
                      status === 'bounced' ? 'red' :
                      'default'
                    }>
                      {status === 'delivered' ? '配信済' :
                       status === 'sent' ? '送信済' :
                       status === 'bounced' ? 'バウンス' :
                       status}
                    </Tag>
                  ),
                },
                {
                  title: '開封',
                  dataIndex: 'opened_at',
                  width: 80,
                  render: (date) => date ? (
                    <Tooltip title={dayjs(date).format('M/D HH:mm')}>
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    </Tooltip>
                  ) : '-',
                },
                {
                  title: 'クリック',
                  dataIndex: 'clicked_at',
                  width: 80,
                  render: (date) => date ? (
                    <Tooltip title={dayjs(date).format('M/D HH:mm')}>
                      <CheckCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  ) : '-',
                },
                {
                  title: 'ログイン',
                  dataIndex: 'logged_in_after',
                  width: 80,
                  render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '-',
                },
                {
                  title: 'リサーチ',
                  dataIndex: 'researched_after',
                  width: 80,
                  render: (v) => v ? <CheckCircleOutlined style={{ color: '#52c41a' }} /> : '-',
                },
              ]}
            />

            <Divider>メール本文プレビュー</Divider>

            <div 
              className="campaign-detail-preview"
              style={{ 
                border: '1px solid #e8e8e8', 
                borderRadius: 8, 
                padding: 16, 
                maxHeight: 300, 
                overflow: 'auto',
                background: '#fafafa'
              }}
              dangerouslySetInnerHTML={{ __html: selectedCampaign.html_content }}
            />
          </>
        )}
      </Modal>

      {/* 画像モーダル */}
      <Modal
        title="画像ライブラリ"
        open={imageModalVisible}
        onCancel={() => setImageModalVisible(false)}
        width={800}
        footer={null}
      >
        <div style={{ marginBottom: 16 }}>
          <Upload
            accept="image/*"
            showUploadList={false}
            customRequest={async ({ file, onSuccess, onError }) => {
              try {
                await handleImageUpload(file);
                onSuccess();
              } catch (error) {
                onError(error);
              }
            }}
          >
            <Button icon={<PlusOutlined />}>画像をアップロード</Button>
          </Upload>
          <Text type="secondary" style={{ marginLeft: 16 }}>
            クリックでURLをコピー
          </Text>
        </div>
        
        <Row gutter={[16, 16]}>
          {images.map(image => (
            <Col key={image.id} span={6}>
              <Card
                hoverable
                cover={
                  <img
                    alt={image.alt_text || image.original_filename}
                    src={image.public_url}
                    style={{ height: 120, objectFit: 'cover' }}
                  />
                }
                size="small"
                onClick={() => {
                  navigator.clipboard.writeText(image.public_url);
                  message.success('画像URLをコピーしました');
                }}
              >
                <Card.Meta
                  description={
                    <Text ellipsis style={{ fontSize: 12 }}>
                      {image.original_filename}
                    </Text>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>

        {images.length === 0 && (
          <Empty description="画像がありません" />
        )}
      </Modal>

      {/* AI生成モーダル */}
      <Modal
        title={
          <Space>
            <RobotOutlined />
            AIでメール生成
          </Space>
        }
        open={aiGenerateModalVisible}
        onCancel={() => setAiGenerateModalVisible(false)}
        onOk={handleAiGenerate}
        okText="生成する"
        confirmLoading={aiLoading}
        width={600}
        destroyOnClose
      >
        <Form form={aiGenerateForm} layout="vertical">
          <Form.Item
            name="purpose"
            label="メールの目的"
            rules={[{ required: true, message: '目的を入力してください' }]}
          >
            <Select placeholder="目的を選択...">
              <Option value="離脱防止">離脱防止（休眠ユーザー向け）</Option>
              <Option value="新機能お知らせ">新機能お知らせ</Option>
              <Option value="キャンペーン告知">キャンペーン・セール告知</Option>
              <Option value="フォローアップ">フォローアップ（利用促進）</Option>
              <Option value="アップセル提案">アップセル・プランアップグレード提案</Option>
              <Option value="お礼・感謝">お礼・感謝メッセージ</Option>
              <Option value="ウェルカム">ウェルカムメール（新規ユーザー）</Option>
              <Option value="その他">その他（詳細を下記に記載）</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="tone"
            label="トーン"
            initialValue="neutral"
          >
            <Select>
              <Option value="formal">フォーマル（丁寧・堅め）</Option>
              <Option value="neutral">ニュートラル（標準）</Option>
              <Option value="casual">カジュアル（親しみやすい）</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="additional_instructions"
            label="追加指示（任意）"
          >
            <TextArea 
              placeholder="特定の機能について言及、特別なオファーの詳細など..." 
              rows={3}
            />
          </Form.Item>

          {selectedPreset && (
            <Alert
              message={`ターゲット: ${presets.find(p => p.name === selectedPreset)?.display_name || selectedPreset}`}
              type="info"
              showIcon
              style={{ marginTop: -8 }}
            />
          )}
        </Form>
      </Modal>

      {/* AIリライトモーダル */}
      <Modal
        title={
          <Space>
            <ReloadOutlined />
            AIでリライト
          </Space>
        }
        open={aiRewriteModalVisible}
        onCancel={() => setAiRewriteModalVisible(false)}
        onOk={handleAiRewrite}
        okText="リライトする"
        confirmLoading={aiLoading}
        width={600}
        destroyOnClose
      >
        <Form form={aiRewriteForm} layout="vertical">
          <Alert
            message="現在のメールをリライトします"
            description={
              <div>
                <div><strong>件名:</strong> {subject?.substring(0, 40)}{subject?.length > 40 ? '...' : ''}</div>
                <div><strong>本文:</strong> {htmlContent?.replace(/<[^>]+>/g, '').substring(0, 60)}...</div>
              </div>
            }
            type="info"
            style={{ marginBottom: 16 }}
          />

          <Form.Item
            name="directions"
            label="リライトの方向性"
            rules={[{ required: true, message: '少なくとも1つ選択してください' }]}
          >
            <Checkbox.Group>
              <Row gutter={[8, 8]}>
                <Col span={12}>
                  <Checkbox value="shorter">より簡潔に</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="polite">より丁寧に</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="friendly">より親しみやすく</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="stronger_cta">CTAを強化</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="urgent">緊急感を出す</Checkbox>
                </Col>
                <Col span={12}>
                  <Checkbox value="benefit_focused">ベネフィット重視</Checkbox>
                </Col>
              </Row>
            </Checkbox.Group>
          </Form.Item>

          <Form.Item
            name="custom_instruction"
            label="追加指示（任意）"
          >
            <TextArea 
              placeholder="具体的なリライトの指示があれば..." 
              rows={2}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default EmailMarketing;
